import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Handshake, Globe, Building2 } from 'lucide-react';
import { cn } from 'src/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { getInitials, paletteForSeed } from 'src/lib/avatar';
import { formatExperienceYears } from './tabFormatters';

// Avatar-initials + name + email + recent-job-info cell, replacing the
// separate bare Name/Email columns — matches the screenshot's "Candidate"
// column. Used by candidateCellColumn below, which every pipeline tab
// (default / secondary / senior_level_flow) mounts, so this one place
// covers "all tabs".
//
// Recent Job Info (most-recent company + role) and total experience render
// as a third muted line under the email — the "SRE · PhonePe · 8 yrs"
// subtitle this cell's design always anticipated but had no data for until
// the listing API added it. Field names confirmed against a real
// candidate-list response. The line is still gated on the data being
// present, so a row missing any of the three just drops that piece.
//
// A per-row status (for senior_level_flow's merged "All statuses" tabs) is
// deliberately NOT shown here — it rendered inline next to the name at
// first, but that's already the most crowded cell in the row (avatar + name
// + email + recent job), so it moved to RowActionsCell.jsx instead, as a
// small colored line above that cell's action buttons. See that file's own
// comment for the current design.
const RECENT_JOB_FIELDS = {
  company: 'job_organization',
  role: 'job_title',
  experienceYears: 'total_experience_years',
};

// Where this candidate's profile actually came from (api field:
// candidate_source, the backend's CandidateSourceType enum) — shown as a
// small badge on the avatar's corner rather than its own column, since the
// field is available on every tab's row data, not just one. Solid-filled
// (not the soft-tonal bg-*-50/text-*-700 pairing used elsewhere in this app)
// with an always-white icon — a badge this small reads as a single colored
// dot either way, so distinguishing sources depends entirely on the
// background hue itself being bold enough to tell apart at a glance,
// which a pale tonal background doesn't give. violet/blue/teal chosen to
// stay clear of emerald/amber/red, which already carry strong positive/
// warning/negative meaning elsewhere on this page (e.g. RowActionsCell's
// own status badge).
const CANDIDATE_SOURCE_CFG = {
  PARTNER: {
    label: 'Partner Sourced',
    detail: 'Submitted by Partner',
    icon: Handshake,
    badgeBg: 'bg-violet-600',
  },
  DIRECT: {
    label: 'Candidate Portal',
    detail: 'Applied via Portal',
    icon: Globe,
    badgeBg: 'bg-blue-600',
  },
  INTERNAL: {
    label: 'Internal Pool',
    detail: 'Uploaded by TA',
    icon: Building2,
    badgeBg: 'bg-teal-600',
  },
};

// Small badge pinned to the avatar's bottom-right corner — a `border-card`
// ring separates it from the avatar underneath, same technique the Career
// timeline's own current-role dot (InterviewBrief.jsx) uses for a badge
// sitting on top of another element. Unrecognized/missing candidate_source
// renders nothing at all (no fallback badge) rather than a guessed source —
// unlike a wrong color/icon elsewhere, a wrong *source* claim here is
// actively misleading, not just a cosmetic miss.
const SourceAvatarBadge = ({ source }) => {
  const cfg = CANDIDATE_SOURCE_CFG[source];
  if (!cfg) return null;
  const Icon = cfg.icon;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'absolute -bottom-0.5 -right-1 flex h-5 w-5 items-center justify-center p-1 rounded-full border-[2px] border-card',
              cfg.badgeBg
            )}
          >
            <Icon className="h-2.5 w-2.5 text-white" strokeWidth={3} />
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="rounded-md border-none bg-foreground px-2 py-1 text-[11px] font-medium text-background shadow-lg"
          arrowClassName="fill-foreground"
        >
          {cfg.label} · {cfg.detail}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

SourceAvatarBadge.propTypes = {
  source: PropTypes.string,
};

SourceAvatarBadge.defaultProps = {
  source: undefined,
};

const getDisplayName = (row) => {
  if (row.candidate_name) return row.candidate_name;
  return `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-';
};

// role · company · 7y 5m — whatever subset of the three the row actually
// carries, joined with the same middot the rest of this feature uses.
const getRecentJobLine = (row) => {
  const parts = [
    formatExperienceYears(row[RECENT_JOB_FIELDS.experienceYears]),
    row[RECENT_JOB_FIELDS.role],
    row[RECENT_JOB_FIELDS.company],
  ].filter(Boolean);
  return parts.join(' · ');
};

// A name/email/recent-job line, truncated with an ellipsis when it
// overflows — same "only wire up the tooltip when actually clipped" logic
// as TestManagement/TableComponents/DescriptionCell.jsx, so a short value
// never gets a needless hover affordance.
const TruncatedLine = ({ text, className }) => {
  const ref = useRef(null);
  const [clipped, setClipped] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (el) setClipped(el.scrollWidth > el.clientWidth + 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [text]);

  const line = (
    <p ref={ref} className={cn('truncate', className)}>
      {text}
    </p>
  );

  if (!clipped) return line;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{line}</TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="max-w-[260px] rounded-[6px] px-2 py-1 text-[11px] leading-snug"
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

TruncatedLine.propTypes = {
  text: PropTypes.string.isRequired,
  className: PropTypes.string,
};

TruncatedLine.defaultProps = {
  className: undefined,
};

const CandidateCell = ({ row }) => {
  const name = getDisplayName(row);
  const initials = getInitials(name);
  const { bg, text } = paletteForSeed(name || String(row.candidate_id || ''));
  const recentJob = getRecentJobLine(row);

  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <div className="relative shrink-0">
        <span
          className={cn(
            'flex items-center justify-center w-11 h-11 rounded-full text-xs font-semibold',
            bg,
            text
          )}
        >
          {initials}
        </span>
        <SourceAvatarBadge source={row.candidate_source} />
      </div>
      <div className="min-w-0">
        <TruncatedLine
          text={name}
          className="text-sm font-semibold text-foreground-strong"
        />
        {row.email && (
          <TruncatedLine
            text={row.email}
            className="text-xs text-muted-foreground"
          />
        )}
        {recentJob && (
          <TruncatedLine
            text={recentJob}
            className="text-xs text-muted-foreground"
          />
        )}
      </div>
    </div>
  );
};

CandidateCell.propTypes = {
  row: PropTypes.shape({
    candidate_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    candidate_name: PropTypes.string,
    email: PropTypes.string,
    // Recent Job Info — see RECENT_JOB_FIELDS above.
    job_organization: PropTypes.string,
    job_title: PropTypes.string,
    total_experience_years: PropTypes.number,
    // PARTNER / DIRECT / INTERNAL — see CANDIDATE_SOURCE_CFG above.
    candidate_source: PropTypes.string,
  }).isRequired,
};

export const candidateCellColumn = {
  key: 'candidate',
  header: 'Candidate',
  className: 'text-sm',
  // Explicit, not just DataTable's default — this is the one column meant
  // to stay left while every other senior_level_flow column right-aligns
  // (per explicit request, for a consistent layout across tabs), so it's
  // worth being clear this is a deliberate anchor, not an oversight.
  align: 'left',
  // Deliberately no `width` — this is the one column meant to absorb
  // whatever space the other, always-narrow columns (sharedColumns.js'
  // statusColumn/mappedAtColumn, and any per-tab equivalents) don't claim,
  // under DataTable's default `table-fixed` layout. `minWidth` is just a
  // floor so avatar+name+email never gets crushed on a narrow viewport —
  // PipelineDataTable's own overflow-x-auto wrapper handles the rest.
  minWidth: '220px',
  render: (_v, row) => <CandidateCell row={row} />,
};

export default CandidateCell;
